import React, { useState } from "react";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../design/dialog";
import { PlusIcon } from "@heroicons/react/16/solid";
import { Input } from "../../../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../../../design/fieldset";
import { useQuestion } from "../../../contexts/QuestionContext";
import { Checkbox, CheckboxField } from "../../../design/checkbox";
import { Question } from "../../../models/Question";
import { Textarea } from "../../../design/textarea";

interface Props {
  question: Question; // Optional prop to specify the matter type ID
  children?: React.ReactNode;
}

interface Errors {
  answer?: string;
}

const CreateAnswerButton: React.FC<Props> = ({ children, question }) => {
  const { createAnswer } = useQuestion();
  const [isOpen, setIsOpen] = useState(false);
  const [answer, setAnswer] = useState<string>("");
  const [errors, setErrors] = useState<Errors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingMore, setIsCreatingMore] = useState(false);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!answer.trim()) {
      newErrors.text = "A question is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    setIsSaving(true);
    try {
      await createAnswer(question._id, {
        answer,
      });
      clearForm(isCreatingMore);
    } catch (error) {
      console.error("Error creating location:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const clearForm = (blockClose: boolean) => {
    setAnswer("");
    if (!blockClose) {
      setIsOpen(false);
    }
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        {children ?? "Answer"}
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>{question.question}</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Textarea
                  value={answer}
                  rows={4}
                  placeholder="Answer"
                  onChange={(e) => setAnswer(e.target.value)}
                />
                {errors.answer && <ErrorMessage>{errors.answer}</ErrorMessage>}
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions className="justify-between">
          <CheckboxField>
            <Checkbox
              checked={isCreatingMore}
              onChange={(checked) => setIsCreatingMore(checked)}
            />
            <Label>Create more</Label>
          </CheckboxField>
          <div className="flex gap-2">
            <Button plain onClick={() => clearForm(false)}>
              Cancel
            </Button>
            <Button disabled={isSaving} onClick={handleSubmit}>
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateAnswerButton;
